import {css, StyleSheet} from 'aphrodite';
import {Kind, ArgumentNode} from 'query-api-graphql';
import {print as astToString} from 'query-api-graphql';
import React, {useCallback, useMemo, useState} from 'react';
import _ from 'underscore';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert, assertAndContinue} from 'Asserts';
import {sum} from 'BenchlingUtil';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {List} from 'data-types/benchling-type-def/ComplexTypes';
import BenchlingTypeValueInput from 'data-types/components/BenchlingTypeValueInput';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import {buildAnyOfFilterValue} from 'query-api/QueryApiFilters';
import useQueryApiIntrospection, {QueryApiIntrospection} from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus, getMostRecentData, NOT_FETCHED_ASYNC_STATE} from 'ui-library/hooks/useAsyncState';
import useThrottle from 'ui-library/hooks/useThrottle';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {stepperStyles, StepperV2} from 'ui-library/stepper/Stepper';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';

import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {
  ColumnMappingStepperStepProps,
  getColumnMappingStepperStepParams,
} from '../column-mapping/ColumnMappingStepperStep';
import {
  getTemplateDataFrameColumns,
  selectedColumnsToColumnMappings,
  unselectColumnsIfColumnIsNotInTemplateColumns,
} from '../column-mapping/ColumnMappingUtil';
import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from '../DataFrameCreationConstants';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import {getSharedDataFrameCreationMethodProps, openSelectedItemForNewStep} from '../DataFrameCreationUtils';
import {useAutoUpdatePreview} from '../hooks/useAutoUpdatePreview';
import DataFrameNameField from '../steps/DataFrameNameField';
import buildQueryApiQueryAst from './buildQueryApiQueryAst';
import formStateToHumanReadableString from './formStateToHumanReadableString';
import GraphQLFieldSelectorWrapper from './GraphQLFieldSelectorWrapper';
import QueryApiAllSchemaSelector, {QueryApiSchemaSelectorSchema} from './QueryApiAllSchemaSelector';
import {FormState} from './RegistrySchemaDataFrameCreationTypes';
import {
  DEFAULT_FIELD_SELECTION,
  FORM_STATE_THROTTLE_DURATION,
  MAX_NUM_QUERY_API_FIELDS,
  PLATE_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT,
} from './SchemaDatasetCreationConstants';
import {
  assertHasArchiveReasonArg,
  buildErrorAndWarningMessages,
  getFieldDisplayNameFn,
  getPreviewFnForDatasetFromQueryApi,
  PreviewState,
  typeHasDefaultField,
} from './SchemaDatasetCreationUtils';

const DEFAULT_QUERY_FORM_STATE = {entityListingFieldName: null};
const plateSchemaFormGlobalState = makeUseGlobalClientState<FormState>(DEFAULT_QUERY_FORM_STATE);
const plateIdsGlobalState = makeUseGlobalClientState<Array<string>>([]);
const previewGlobalState = makeUseGlobalClientState<PreviewState>(NOT_FETCHED_ASYNC_STATE);
const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

const DEFAULT_DATA_FRAME_NAME_STATE = {
  name: '',
  hasUserEdits: false,
};
const dataFrameNameGlobalState = makeUseGlobalClientState<DataFrameNameState>(DEFAULT_DATA_FRAME_NAME_STATE);

// Capped for performance reasons, we can do performance testing if we need to raise it.
const MAX_SELECTED_PLATE_IDS = 10;
interface PlateSchemaSelectorStepperProps {
  dataFrameNameState: DataFrameNameState;
  setDataFrameNameState: (state: DataFrameNameState) => void;
  setQueryFormState: (state: FormState) => void;
  queryApiIntrospection: QueryApiIntrospection;
  queryFormState: FormState;
  selectedPlateIds: Array<string>;
  setSelectedPlateIds: (plateIds: Array<string>) => void;
  columnMappingProps?: ColumnMappingStepperStepProps;
}

function PlateSchemaSelectorStepper({
  columnMappingProps,
  dataFrameNameState,
  setDataFrameNameState,
  setQueryFormState,
  queryApiIntrospection,
  queryFormState,
  selectedPlateIds,
  setSelectedPlateIds,
}: PlateSchemaSelectorStepperProps): JSX.Element {
  const queryListingField =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryField(queryFormState.entityListingFieldName);

  const schemaId =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryFieldSchemaId(queryFormState.entityListingFieldName);

  function plateIdsToArgumentNode(plateIds: Array<string>): ArgumentNode {
    return {
      kind: Kind.ARGUMENT,
      name: {
        kind: Kind.NAME,
        value: 'id',
      },
      value: buildAnyOfFilterValue(plateIds),
    };
  }

  const handleChangeSchema = useCallback(
    (schema: QueryApiSchemaSelectorSchema | null): void => {
      if (schema == null) {
        setQueryFormState({entityListingFieldName: null});
        return;
      }

      const listingField = queryApiIntrospection.getQueryListingFieldForSchema(schema.id);
      assert(listingField != null, 'User selected a schema without a corresponding Query API listing field');

      const errorLoggingData = {
        schemaId: schema.id,
        schemaName: schema.name,
        listingFieldName: listingField.name,
      };

      assertHasArchiveReasonArg(listingField, errorLoggingData);

      setSelectedPlateIds([]);
      setQueryFormState({
        entityListingFieldName: listingField.name,
        entityFilterArguments: [plateIdsToArgumentNode([])],
        entityFieldSelections: {
          kind: Kind.SELECTION_SET,
          selections: typeHasDefaultField(listingField.type) ? [DEFAULT_FIELD_SELECTION] : [],
        },
        relatedResultsFormStates: [],
      });

      if (!dataFrameNameState.hasUserEdits) {
        setDataFrameNameState({...dataFrameNameState, name: `${schema.name} data`});
      }
    },
    [queryApiIntrospection, setQueryFormState, dataFrameNameState, setDataFrameNameState, setSelectedPlateIds]
  );

  return (
    <StepperV2
      steps={[
        {
          title: <div className={css(stepperStyles.defaultStepTitle)}>Select plate schema</div>,
          content: (
            <div className={css(stepperStyles.defaultStepContent)}>
              <QueryApiAllSchemaSelector
                schemaId={schemaId}
                handleSchemaChange={handleChangeSchema}
                queryApiIntrospection={queryApiIntrospection}
                schemaFilter={(schema) => APIUtil.isPlateSchema(schema.id)}
              />
            </div>
          ),
          key: 'plate-schema-selector',
        },
        schemaId != null &&
          queryFormState.entityListingFieldName != null && {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Select plates</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                {selectedPlateIds.length > MAX_SELECTED_PLATE_IDS && (
                  <div className={css(styles.bannerWrapper)}>
                    <InlineBanner
                      bannerType={InlineBannerType.ERROR}
                      text={`At most ${MAX_SELECTED_PLATE_IDS} plates can be selected.`}
                    />
                  </div>
                )}
                <BenchlingTypeValueInput
                  typeDef={new List(LeafType.PLATE)}
                  allowAutoFocus={false}
                  onChange={(newSelection) => {
                    if (newSelection == null) {
                      setSelectedPlateIds([]);
                    } else {
                      assertAndContinue(Array.isArray(newSelection), 'Expected an array of plate IDs');
                      const newSelectedPlateIds = _.unique(newSelection as Array<string>);
                      const plateIdsArg = plateIdsToArgumentNode(newSelectedPlateIds);

                      setQueryFormState({
                        ...queryFormState,
                        entityFilterArguments: [plateIdsArg],
                      });

                      setSelectedPlateIds(_.unique(newSelection as Array<string>));
                    }
                  }}
                  value={selectedPlateIds}
                  schemaId={schemaId}
                  hideSelectedResults={true}
                />
              </div>
            ),
            key: 'plates-selector',
          },
        schemaId != null &&
          queryFormState.entityListingFieldName != null &&
          queryListingField != null && {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Select fields</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                <GraphQLFieldSelectorWrapper
                  field={queryListingField}
                  logicalDepthLimit={PLATE_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT}
                  selectionSet={queryFormState.entityFieldSelections}
                  setSelectionSet={(newSelectionSet) => {
                    setQueryFormState({...queryFormState, entityFieldSelections: newSelectionSet});
                  }}
                  getFieldDisplayName={getFieldDisplayNameFn(queryListingField, queryApiIntrospection)}
                  {...pick(
                    queryApiIntrospection,
                    'getTypeDisplayName',
                    'graphQLSchema',
                    'isArchivedField',
                    'isBacklinkField',
                    'isUserDefinedField',
                    'isUserComputedField'
                  )}
                />
              </div>
            ),
            key: 'fields-selector',
          },
        columnMappingProps != null &&
          schemaId != null &&
          queryFormState.entityListingFieldName != null &&
          queryListingField != null &&
          getColumnMappingStepperStepParams({...columnMappingProps, key: 'column-mapping-step'}),
        schemaId != null &&
          queryFormState.entityListingFieldName != null &&
          queryListingField != null && {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Name table</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                <DataFrameNameField
                  {...dataFrameNameState}
                  onNameChange={(newName: string) => {
                    setDataFrameNameState({name: newName, hasUserEdits: true});
                  }}
                />
              </div>
            ),
            key: 'name-table',
          },
      ]}
    />
  );
}

export default function PlateSchemaDataFrameCreationView({
  analysis,
  clientStateId,
  onOpenStepInitializationView,
  onCloseStepInitializationView,
  selectedItem,
}: MainViewComponentProps<AnalysisMainViewItemType.PLATE_SCHEMA_DATASET_CONFIGURATION>): JSX.Element {
  const queryApiIntrospection = useQueryApiIntrospection();
  const [queryFormState, setQueryFormState] = plateSchemaFormGlobalState.useGlobalClientState(clientStateId);
  const [previewState, setPreviewState] = previewGlobalState.useGlobalClientState(clientStateId);
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);
  const [dataFrameNameState, setDataFrameNameState] =
    dataFrameNameGlobalState.useGlobalClientState(clientStateId);
  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(analysis, selectedItem.stepIdToReplace),
    [analysis, selectedItem.stepIdToReplace]
  );
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [isManuallyChanged, setIsManuallyChanged] = useState<boolean>(false);

  const [selectedPlateIds, setSelectedPlateIds] = plateIdsGlobalState.useGlobalClientState(clientStateId);

  // Don't generate a query AST if there are no plates selected, as results will be empty and trying to
  // generate a preview in this case is confusing.
  const queryAst = useMemo(
    () => (selectedPlateIds.length > 0 ? buildQueryApiQueryAst(queryFormState) : null),
    [queryFormState, selectedPlateIds]
  );
  const throttledQueryAst = useThrottle(queryAst, FORM_STATE_THROTTLE_DURATION);

  const canFinalize =
    queryApiIntrospection.status === QueryResultType.SUCCESS &&
    queryAst != null &&
    queryApiIntrospection.getQueryAstValidationErrors(queryAst).length === 0 &&
    queryFormState.entityListingFieldName != null &&
    selectedPlateIds.length <= MAX_SELECTED_PLATE_IDS &&
    dataFrameNameState.name !== '';

  const previewDataFrame = getMostRecentData(previewState)?.dataFrame ?? null;

  if (previewDataFrame?.columns) {
    unselectColumnsIfColumnIsNotInTemplateColumns(
      selectedColumns,
      previewDataFrame.columns,
      setSelectedColumns
    );
  }

  function handleClearState(): void {
    setDataFrameNameState(DEFAULT_DATA_FRAME_NAME_STATE);
    setQueryFormState(DEFAULT_QUERY_FORM_STATE);
    setPreviewState(NOT_FETCHED_ASYNC_STATE);
    setSelectedPlateIds([]);
  }

  async function onFinalize(): Promise<void> {
    assert(queryAst != null && dataFrameNameState.name !== '', 'Tried to finalize with incomplete form');

    setIsFinalizing(true);
    try {
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        analysis.id,
        {
          stepType: PipelineStepType.PLATE_SCHEMA_DATASET,
          stepVersion: 0,
          stepConfig: {
            dataFrameName: dataFrameNameState.name,
            plateIds: selectedPlateIds,
            query: astToString(queryAst),
            queryDescription: await formStateToHumanReadableString(queryFormState, queryApiIntrospection),
          },
        },
        selectedItem.stepIdToReplace
          ? {
              originalStepId: selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
            }
          : undefined
      );
      openSelectedItemForNewStep(newStep, onCloseStepInitializationView);
      handleClearState();
      setIsFinalizing(false);
    } catch (err) {
      setIsFinalizing(false);
      throw err;
    }
  }

  useAutoUpdatePreview(
    throttledQueryAst == null ? null : {queryAst: throttledQueryAst},
    getPreviewFnForDatasetFromQueryApi(queryApiIntrospection, previewState),
    setPreviewState
  );

  const handleChangeQueryFormState = useCallback(
    (newFormState: FormState) => {
      const totalFields =
        newFormState.entityListingFieldName == null
          ? 0
          : countSelectedFields(newFormState.entityFieldSelections.selections) +
            sum(
              newFormState.relatedResultsFormStates.map((resultFormState) =>
                countSelectedFields(resultFormState.backlinkSelections.selections)
              )
            );
      if (totalFields > MAX_NUM_QUERY_API_FIELDS) {
        notifications.error(`Only ${MAX_NUM_QUERY_API_FIELDS} column selections are allowed.`, {
          duration: 4,
        });
      } else {
        setQueryFormState(newFormState);
      }
    },
    [setQueryFormState]
  );

  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(selectedItem)}
      clientStateId={clientStateId}
      dataFrames={previewDataFrame == null ? [] : [{...previewDataFrame, name: dataFrameNameState.name}]}
      disableFinalizeButton={!canFinalize}
      isFinalizing={isFinalizing}
      isLoadingPreview={previewState.status === AsyncStatus.LOADING}
      messages={buildErrorAndWarningMessages(previewState)}
      noPreviewPlaceholderOptions={{
        title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
        subtitle: 'Select a plate schema to see a preview of your table.',
      }}
      defaultDataFrameTableMode={DataFrameTableMode.TEXT}
      onCancel={() => {
        handleClearState();
        onCloseStepInitializationView();
      }}
      onFinalize={onFinalize}
      title="Create from Plates"
      toolbar={
        <ChooseDifferentDataFrameCreationMethodButton
          onOpenStepInitializationView={(newSelectedItem: {
            type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
          }) => {
            handleClearState();
            onOpenStepInitializationView({
              ...newSelectedItem,
              stepIdToReplace: selectedItem.stepIdToReplace,
            });
          }}
        />
      }
    >
      {queryApiIntrospection.status === QueryResultType.ERROR ? (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading plate schemas." />
      ) : queryApiIntrospection.status === QueryResultType.PENDING ? (
        <CenteredHorizontalThrobber />
      ) : (
        <PlateSchemaSelectorStepper
          queryApiIntrospection={queryApiIntrospection}
          queryFormState={queryFormState}
          setQueryFormState={handleChangeQueryFormState}
          selectedPlateIds={selectedPlateIds}
          setSelectedPlateIds={setSelectedPlateIds}
          dataFrameNameState={dataFrameNameState}
          setDataFrameNameState={setDataFrameNameState}
          columnMappingProps={
            selectedItem.stepIdToReplace != null
              ? {
                  templateDataFrameColumns,
                  newDataFrameColumns: previewDataFrame?.columns ?? null,
                  newDataFrameState: previewState.status,
                  selectedColumns,
                  setSelectedColumns,
                  isManuallyChanged,
                  setIsManuallyChanged,
                }
              : undefined
          }
        />
      )}
    </StepConfigurationViewTemplate>
  );
}

const styles = StyleSheet.create({
  bannerWrapper: {
    marginBottom: 5,
  },
});
